import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { useDesignStore } from '../../store/designStore';
import { useToast, useConfirm } from '../Notifications';
import type { FabricCanvas } from '../../types/fabric';
import { safeGetItem } from '../../lib/safeStorage';
import { ToolSection } from './shared/ToolSection';

interface MyDesignsToolProps {
  canvas: FabricCanvas | null;
}

function authHeaders(): Record<string, string> {
  const token = safeGetItem('session', 'customedge_token');
  if (token) return { Authorization: `Bearer ${token}` };

  // No JWT — Magento's design-launcher.js only ever sends `?hash=`, never a
  // real customer token (`mtoken`), so for every customer arriving from the
  // PDP popup (logged in or not) this is the only identity signal that will
  // ever exist. Fall back to the "My Design" retention hash useDesignerInit.ts
  // persisted from that param — see backend/src/routes/designs.ts's
  // getDesignOwnerFilter() for how both the guest form
  // (`promofit_guest_{sessionId}`) and the customer form
  // (`promofit_{magentoCustomerId}`) are resolved and why the latter is
  // trusted despite not being a real bearer token (2026-07-06 product
  // decision, after logged-in customers' designs were found silently
  // unretrievable with no fallback).
  const hash = safeGetItem('session', 'customedge_design_hash');
  return hash ? { 'X-Design-Hash': hash } : {};
}

type FilterMode = 'current' | 'all' | 'sku' | 'order';

export function MyDesignsTool(_props: MyDesignsToolProps) {
  const { t } = useTranslation();
  const showToast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const { loadFromTemplate, setSavedDesignId, setSelectedVariantOptions, productConfig } = useDesignStore();
  const currentProductId = productConfig?.productId;
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [allDesigns, setAllDesigns] = useState<any[]>([]);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // Default to scoping the panel to whatever product is actively being
  // edited — opening My Designs from a T-shirt should show that T-shirt's
  // designs, not every design across every product the customer ever made.
  // Falls back to 'all' if there's no product context (e.g. panel opened
  // outside a product designer session), same as the previous default.
  const [filterMode, setFilterMode] = useState<FilterMode>(currentProductId ? 'current' : 'all');
  const [filterValue, setFilterValue] = useState<string>('');

  const token = safeGetItem('session', 'customedge_token');
  const designHash = safeGetItem('session', 'customedge_design_hash');
  // Either identity is enough to list designs — a guest with a live Magento
  // session (hash present, no JWT yet) should see their own in-progress
  // designs, not a "please sign in" wall they have no way to act on here.
  const hasIdentity = !!token || !!designHash;

  // Unfiltered fetch (limit high enough to cover realistic My Designs sizes)
  // purely to build the SKU / order-number dropdown option lists from the
  // customer's own actual data — "Select an option" only ever making sense
  // as a real, populated list, not free text (per product decision: a
  // dropdown avoids typos/guessing since it can only ever offer values that
  // genuinely exist). Kept separate from the filtered fetch below so
  // switching the filter doesn't need a second round trip just to know what
  // options exist.
  const { data: unfilteredData } = useQuery({
    queryKey: ['my-designs-options', token, designHash],
    queryFn: () => fetch('/api/designs?limit=100', { headers: authHeaders() }).then(r => r.json()),
    enabled: hasIdentity,
    staleTime: 30_000,
  });

  const skuOptions = React.useMemo<string[]>(() => {
    const skus: string[] = (unfilteredData?.designs ?? []).map((d: any) => d.productId).filter(Boolean);
    return Array.from(new Set(skus)).sort();
  }, [unfilteredData]);

  const orderOptions = React.useMemo<string[]>(() => {
    const orders: string[] = (unfilteredData?.designs ?? []).flatMap((d: any) => d.orderIds ?? []);
    return Array.from(new Set(orders)).sort().reverse();
  }, [unfilteredData]);

  // Reset the picked value whenever the filter mode itself changes — a SKU
  // selected under "SKU" mode is meaningless (and would silently over-filter)
  // if left set after switching to "Order Number".
  const handleFilterModeChange = (mode: FilterMode) => {
    setFilterMode(mode);
    setFilterValue('');
    setCursor(undefined);
  };

  const filterQueryParam =
    filterMode === 'current' && currentProductId ? `&sku=${encodeURIComponent(currentProductId)}` :
    filterMode === 'sku' && filterValue ? `&sku=${encodeURIComponent(filterValue)}` :
    filterMode === 'order' && filterValue ? `&orderId=${encodeURIComponent(filterValue)}` :
    '';

  // `allDesignsKey` tracks which filter the *rendered* `allDesigns` belongs
  // to. Switching filters used to be handled by two independent effects —
  // one appending fetched `data` into `allDesigns`, a separate one resetting
  // `allDesigns` to `[]` whenever the filter changed — and they raced: React
  // Query treats a filter switch as a brand-new query key, so there was a
  // window where the reset effect had already cleared `allDesigns` but the
  // new key's fetch hadn't resolved yet. `isLoading` in TanStack Query v5
  // only reflects *that exact key* never having had cached data before, so a
  // fast response could land `isLoading: false` in that exact window and the
  // empty-state message ("You don't have any saved designs yet.") would
  // flash before the real designs reappeared — reproduced live: switching
  // "This product" -> "All" repeatedly reliably triggered it, and confirmed
  // it disappears with this fix. Fix: derive "still switching" directly by
  // comparing the key `allDesigns` was last populated for against the key
  // currently selected — a plain render-time comparison instead of a second
  // effect independently racing the first for the same state.
  const filterKey = `${filterMode}:${filterValue}:${currentProductId ?? ''}`;
  const [allDesignsKey, setAllDesignsKey] = useState(filterKey);
  const isSwitchingFilter = allDesignsKey !== filterKey;

  const { data, isFetching } = useQuery({
    queryKey: ['my-designs', cursor, token, designHash, filterMode, filterValue, currentProductId],
    queryFn: () =>
      fetch(`/api/designs?${cursor ? `cursor=${cursor}` : ''}${filterQueryParam}`, { headers: authHeaders() }).then(r => r.json()),
    enabled: hasIdentity,
    staleTime: 30_000,
  });

  React.useEffect(() => {
    if (!data?.designs) return;
    setAllDesigns(prev => (cursor && !isSwitchingFilter) ? [...prev, ...data.designs] : data.designs);
    setAllDesignsKey(filterKey);
  }, [data, cursor, filterKey, isSwitchingFilter]);

  const isLoading = isFetching || isSwitchingFilter;

  if (!hasIdentity) {
    return (
      <div style={{ padding: 16 }}>
        <p style={{ fontSize: 14, color: 'var(--text-muted)', textAlign: 'center' }}>{t('myDesigns.loginRequired')}</p>
      </div>
    );
  }

  const loadDesign = async (design: any) => {
    setLoadingId(design.id);
    try {
      const detail = await fetch(`/api/designs/${design.id}`, { headers: authHeaders() }).then(r => r.json());
      if (!detail.jsonObjects) throw new Error('No canvas data');
      loadFromTemplate(detail.jsonObjects);
      setSavedDesignId(design.id);
      // Restore the Object Variant selection saved with the design (inside
      // productInfo) so the 3D preview reopens on the customer's picked
      // variant, not the model's default appearance.
      try {
        const saved = JSON.parse(detail.productInfo ?? '{}').selectedVariantOptions;
        if (saved && Object.keys(saved).length) setSelectedVariantOptions(saved);
      } catch { /* malformed productInfo — keep defaults */ }
      showToast(t('myDesigns.load'), 'success');
    } catch {
      showToast(t('myDesigns.loadFailed'), 'error');
    } finally {
      setLoadingId(null);
    }
  };

  const deleteDesign = async (design: any) => {
    const ok = await confirm(t('myDesigns.confirmDelete', { name: design.title ?? t('myDesigns.untitled') }), { danger: true });
    if (!ok) return;
    setDeletingId(design.id);
    try {
      const res = await fetch(`/api/designs/${design.id}`, { method: 'DELETE', headers: authHeaders() });
      if (!res.ok) throw new Error('delete failed');
      setAllDesigns(prev => prev.filter(d => d.id !== design.id));
      qc.invalidateQueries({ queryKey: ['my-designs'] });
      showToast(t('myDesigns.deleteSuccess'), 'success');
    } catch {
      showToast(t('myDesigns.deleteFailed'), 'error');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div style={{ padding: 12, display: 'flex', flexDirection: 'column' }}>
      <ToolSection title={t('myDesigns.sectionFilter')} divider={false}>
        <div style={{ display: 'flex', gap: 6 }}>
          <select
            value={filterMode}
            onChange={e => handleFilterModeChange(e.target.value as FilterMode)}
            style={{ flex: '0 0 auto', fontSize: 12, padding: '4px 6px' }}
          >
            {currentProductId && <option value="current">{t('myDesigns.filterCurrent')}</option>}
            <option value="all">{t('myDesigns.filterAll')}</option>
            <option value="sku">{t('myDesigns.filterBySku')}</option>
            <option value="order">{t('myDesigns.filterByOrder')}</option>
          </select>

          {filterMode === 'sku' && (
            <select
              value={filterValue}
              onChange={e => { setFilterValue(e.target.value); setCursor(undefined); }}
              style={{ flex: 1, fontSize: 12, padding: '4px 6px', minWidth: 0 }}
            >
              <option value="">{t('myDesigns.filterSkuPlaceholder')}</option>
              {skuOptions.map((sku: string) => <option key={sku} value={sku}>{sku}</option>)}
            </select>
          )}

          {filterMode === 'order' && (
            <select
              value={filterValue}
              onChange={e => { setFilterValue(e.target.value); setCursor(undefined); }}
              style={{ flex: 1, fontSize: 12, padding: '4px 6px', minWidth: 0 }}
            >
              <option value="">{t('myDesigns.filterOrderPlaceholder')}</option>
              {orderOptions.map((orderId: string) => <option key={orderId} value={orderId}>{orderId}</option>)}
            </select>
          )}
        </div>
      </ToolSection>

      <ToolSection title={t('myDesigns.sectionDesigns')}>
        {isLoading && allDesigns.length === 0 && (
          <p style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', padding: 16 }}>
            {t('myDesigns.loading')}
          </p>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
          {allDesigns.map((d: any) => (
            <div key={d.id} style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <button
                onClick={() => loadDesign(d)}
                disabled={loadingId === d.id}
                style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0, opacity: loadingId === d.id ? 0.6 : 1 }}
                title={t('myDesigns.load')}
              >
                {d.thumbnail_url ? (
                  <img src={d.thumbnail_url} alt={d.title ?? ''} style={{ width: '100%', aspectRatio: '1', objectFit: 'cover' }} loading="lazy" />
                ) : (
                  <div style={{ width: '100%', aspectRatio: '1', background: '#f1f5f9' }} />
                )}
              </button>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 6px' }}>
                <span style={{ fontSize: 12, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                  {d.title ?? t('myDesigns.untitled')}
                </span>
                <button
                  onClick={() => deleteDesign(d)}
                  disabled={deletingId === d.id}
                  title={t('common.delete')}
                  style={{ display: 'flex', alignItems: 'center', background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '0 0 0 4px', opacity: deletingId === d.id ? 0.5 : 1 }}
                >
                  <Trash2 size={13} strokeWidth={1.75} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {!isLoading && allDesigns.length === 0 && (
          <p style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', padding: '16px 0' }}>
            {t('myDesigns.empty')}
          </p>
        )}

        {data?.loadMore === 1 && (
          <button onClick={() => setCursor(data.nextCursor)} className="btn btn-secondary" style={{ width: '100%', fontSize: 12 }}>
            {t('myDesigns.loadMore')}
          </button>
        )}
      </ToolSection>
    </div>
  );
}
